/* eslint-disable complexity */

import {VISIBILITY_STATE} from '@/constants/common';
import {PerformanceLogger} from '../performance';
import {CATEGORY, LOG_DOC_TYPE, LOG_TYPE} from './const';
import {ENVIRONMENT_LEVEL} from '@/Constant';

const DOC_PAINT_THRESHOLD = (window as any).wikiGlobal?.docPaintThreshold || 5000;


export type CategoryType = keyof typeof CATEGORY;

export enum DOC_END_TYPE {
    rte = 'rte',
    morphoFsPaint = 'morphoFsPaint',
    morphoPaint = 'morphoPaint',
}

function createDocLoadTimeData(time: number) {
    return {
        [`${DOC_END_TYPE.rte}`]: time,
        [`${DOC_END_TYPE.morphoFsPaint}`]: time,
        [`${DOC_END_TYPE.morphoPaint}`]: time,
    };
}

const WEB_LOG_MAP = {
    [`${DOC_END_TYPE.rte}`]: '80001.2.10',
    [`${DOC_END_TYPE.morphoFsPaint}`]: '80001.2.39',
    [`${DOC_END_TYPE.morphoPaint}`]: '80001.2.40',
};

const PERFORMANCE_PREFIX = 'x_rlkb_document_';

const MEASURE = {
    start: 'start',
    end: 'end',
};

let isPageHidden = false;
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === VISIBILITY_STATE.hidden) {
        isPageHidden = true;
    }
    if (document.visibilityState === VISIBILITY_STATE.visible) {
        isPageHidden = false;
    }
});
export class DocPagePaint {

    key = 'DocPagePaint';

    logger: PerformanceLogger;

    category = CATEGORY;

    pageLoaded = false;

    docLoadTime = createDocLoadTimeData(0);

    firstSpaNav = true;

    docGuid = '';

    timeoutHandle: NodeJS.Timeout | null = null;

    time: {[key: string]: any} | null = null;

    constructor(logger: PerformanceLogger) {
        this.logger = logger;
    }

    logWeb =({id, data}: {id: string, data?: any}) => {
        const logger = this.logger;
        const logLevel = logger?.logLevel || '';
        if ([ENVIRONMENT_LEVEL.online, ENVIRONMENT_LEVEL.gray, ENVIRONMENT_LEVEL.qa].includes(logLevel)) {
            this.logger.feLogger({
                id,
                data,
            });
        }
    };

    clearTimeOut = () => {
        const hardHandle = (window as any).wikiGlobal?.docPaintHandle;
        const softHandle = this.timeoutHandle;
        hardHandle && clearTimeout(hardHandle);
        const docLoadTime = this.docLoadTime[DOC_END_TYPE.rte];
        /**
         * 编辑器卡死时 这时点击切换到其它页面会导致错误清除定时器
         * 如果执行时 +new Date() - this.docLoadTime 已经超时5s
         * 不清除 依然上报
         */
        if (docLoadTime && (+new Date() - docLoadTime) > DOC_PAINT_THRESHOLD) {
            return;
        }
        softHandle && clearTimeout(softHandle);
    };

    performanceStart = () => {
        try {
            [
                DOC_END_TYPE.morphoFsPaint,
                DOC_END_TYPE.morphoPaint,
            ].forEach(type => {
                performance.mark && performance.mark(`${type}${MEASURE.start}`);
            });
        } catch (e) {
            console.error('e: ', e);
        }
    };

    performanceEnd = (endType: DOC_END_TYPE) => {
        try {
            if ([
                DOC_END_TYPE.morphoFsPaint,
                DOC_END_TYPE.morphoPaint,
            ].includes(endType)) {
                performance.mark && performance.mark(`${endType}${MEASURE.end}`);
                performance.measure && performance.measure(
                    `${PERFORMANCE_PREFIX}${endType}`,
                    `${endType}${MEASURE.start}`,
                    `${endType}${MEASURE.end}`
                );
            }
        } catch (e) {
            console.error('e: ', e);
        }
    };

    performanceClear = () => {
        try {
            [
                DOC_END_TYPE.morphoFsPaint,
                DOC_END_TYPE.morphoPaint,
            ].forEach(type => {
                performance.clearMarks && performance.clearMarks(`${type}${MEASURE.start}`);
            });
        } catch (e) {
            console.error('e: ', e);
        }

    };

    paintStart = ({firstSpaNav, docGuid}: {firstSpaNav: boolean, docGuid: string}) => {
        // 如果是编辑器上报的增加weirwood上报
        this.performanceStart();
        this.firstSpaNav = firstSpaNav;
        this.docGuid = docGuid;
        // 如果是硬加载
        if (firstSpaNav) {
            // 5s延迟不做处理 因为硬加载已经做了开始处理了
            const htmlLoadTime = (window as any).wikiGlobal?.htmlLoadTime;
            this.docLoadTime = createDocLoadTimeData(htmlLoadTime);
            return;
        }
        // 如果是软加载
        this.clearTimeOut();
        this.timeoutHandle = setTimeout(() => {
            // 5s超时后上报一次埋点
            this.logOverTime();
        }, DOC_PAINT_THRESHOLD);
        this.docLoadTime = createDocLoadTimeData(+new Date());

    };

    getType = () => {
        return this.firstSpaNav ? 'hard' : 'soft';
    };

    logOverTime = () => {
        this.logWeb({
            id: '80001.2.9',
            data: {
                type: this.getType(),
            },
        });
    };

    // 计算delay值
    calculateDelay = (endType: DOC_END_TYPE, docLoadTime: number, data?: any) => {
        let delay = +new Date() - docLoadTime;
        // 表格受石墨通知影响，在计算全量dom渲染时，delay需额外减去stableTime
        if (data?.docType === LOG_DOC_TYPE.sheet && endType === DOC_END_TYPE.rte) {
            delay = data.endTime - docLoadTime; // delay - data?.stableTime;
        }
        return delay;
    };

    // data 是编辑器定义的 知识库不关心 rte也没有暴露出来
    paintEnd = (endType: DOC_END_TYPE, docGuid: string, data?: any) => {
        // 如果结束的docGuid与 开始的docGuid不符合 则不上报哦
        if (docGuid !== this.docGuid) {
            return {
                delay: 0,
                type: 'hard',
            };
        }
        const docLoadTime = this.docLoadTime[endType];
        const logger = this.logger;
        const logLevel = logger.logLevel || '';
        // 若已经上报过 则不再上报
        if (!docLoadTime) {
            return {
                delay: 0,
                type: 'hard',
            };
        }
        const delay = this.calculateDelay(endType, docLoadTime, data);
        if (delay <= 0) {
            return {
                delay: 0,
                type: 'hard',
            };
        }
        // 表格需要去除不需要上报的数据
        if (data?.docType === LOG_DOC_TYPE.sheet) {
            // 表格RTE加载时间 添加冗余字段 方便排查问题
            const uploadTime = +new Date();
            const stableTime = data?.stableTime;
            const loadTime = docLoadTime;
            data = {docType: data.docType, sTime: stableTime, uploadTime: uploadTime, loadTime: loadTime};
        }
        const uploadData = {
            delay,
            type: this.getType(),
            ...data,
        };
        // 如果页面被隐藏 不上报
        !isPageHidden && this.logWeb({
            id: WEB_LOG_MAP[endType] || '80001.2.10',
            data: uploadData,
        });
        this.docLoadTime = {
            ...this.docLoadTime,
            [`${endType}`]: 0,
        };
        if (endType === DOC_END_TYPE.rte) {
            delay <= DOC_PAINT_THRESHOLD && this.clearTimeOut();
        }
        // 如果是编辑器上报的增加weirwood上报
        this.performanceEnd(endType);
        if (logLevel !== ENVIRONMENT_LEVEL.online && endType === DOC_END_TYPE.rte) {
            logger.buffer.add(this.key, {
                ...uploadData,
                url: location.href,
            });
        }
        return {
            delay: uploadData.delay,
            type: uploadData.type,
        };
    };

    // 用户切换了页面 无法看出是否渲染成功 需要清空之前的记录
    paintStop = () => {
        this.clearTimeOut();
        this.docLoadTime = createDocLoadTimeData(0);
        // 如果是编辑器上报的增加weirwood上报
        this.performanceClear();
    };

    getData() {
        return {
            type: this.key,
            logType: LOG_TYPE.period,
            origin: this.logger.buffer.get(this.key) || [],
        };
    }

}
